/**
 * The alerts section of the Cloud board (L1): severity donut + 7-day volume
 * line + the worst 5 firing alerts. The full list lives one click deeper at
 * `/cloud/alerts` (dashboard design rules §1 — the altitude test).
 *
 * Honesty: the backend caps the alert *list* at the 50 most severe while the
 * *counts* always describe the whole window. The donut therefore reads from
 * `by_severity` (whole-window truth) unless a subscription filter is applied —
 * subscription splits only exist on the capped list, and the captions say so.
 */

import { Chart } from "@norican/dataviz";
import { Icon } from "@norican/icons";
import { Badge, DataGrid, EmptyState, Typography } from "@norican/react";

import { type BoardRange, ChartCard } from "@/components/board";
import type { CloudAlert, CloudAlerts } from "@/lib/api";
import { formatCount, SEVERITY_CONFIG, SEVERITY_ORDER, severityStatus } from "@/lib/charts/config";
import { DrillLink, useDrillNavigate } from "@/lib/drill";

import { PanelCard } from "./panel-card";
import { ALERT_DRAWER_PARAM, CLOUD_ALERTS_PATH, SUB_PARAM } from "./params";
import { formatTimestamp, resourceNameFromId, shortType } from "./status";

/** How many "worst" alerts the L1 grid shows — the rest live at L2. */
const WORST_LIMIT = 5;

interface SeverityRow {
  severity: string;
  active: number;
  total: number;
}

interface VolumeRow {
  day: string;
  label: string;
  fired: number;
}

function toIsoDay(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

interface AlertsSectionProps {
  alerts: CloudAlerts;
  /** Selected subscription IDs from the board FilterRow ([] = all). */
  subIds: string[];
  /** The board's date window — the volume line is sliced to it (max 7 days). */
  range: BoardRange;
}

export function AlertsSection({ alerts, subIds, range }: AlertsSectionProps) {
  const navigateDrill = useDrillNavigate();

  if (!alerts.configured) {
    return (
      <section id="cloud-alerts" className="scroll-mt-6">
        <PanelCard title="Alerts" caption="Not available">
          <Typography variant="body-sm" tone="muted">
            {alerts.status_detail}
          </Typography>
        </PanelCard>
      </section>
    );
  }

  const scoped = subIds.length > 0;
  const listed = scoped
    ? alerts.alerts.filter((alert) => subIds.includes(alert.subscription_id))
    : alerts.alerts;
  const firing = listed.filter((alert) => alert.monitor_condition === "Fired");

  // Severity mix of alerts firing now. Whole-window counts when unfiltered;
  // counted from the capped list when a subscription filter narrows the view.
  const severityRows: SeverityRow[] = SEVERITY_ORDER.map((severity) => {
    if (!scoped) {
      const entry = alerts.by_severity.find((row) => row.severity === severity);
      return { severity, active: entry?.active ?? 0, total: entry?.total ?? 0 };
    }
    const inSeverity = listed.filter((alert) => alert.severity === severity);
    return {
      severity,
      active: inSeverity.filter((alert) => alert.monitor_condition === "Fired").length,
      total: inSeverity.length,
    };
  });
  const donutRows = severityRows.filter((row) => row.active > 0);
  const activeTotal = donutRows.reduce((sum, row) => sum + row.active, 0);

  // Fired-per-day volume, from the listed alerts (fired_at is per-alert; the
  // whole-window tallies carry no timestamps). Sliced to the board range,
  // never past the endpoint's 7-day window.
  const volumeDays = Math.min(range.days, 7);
  const volumeRows: VolumeRow[] = [];
  for (let offset = volumeDays - 1; offset >= 0; offset -= 1) {
    const date = new Date(range.toDate.getTime() - offset * 86_400_000);
    const iso = toIsoDay(date);
    volumeRows.push({
      day: iso,
      label: date.toLocaleDateString([], { day: "2-digit", month: "short" }),
      fired: listed.filter((alert) => alert.fired_at?.startsWith(iso)).length,
    });
  }

  const capCaveat = alerts.truncated
    ? " — list capped at the most severe, earlier days may undercount"
    : "";

  const drillParams = scoped ? { [SUB_PARAM]: subIds } : {};

  return (
    <section id="cloud-alerts" className="flex scroll-mt-6 flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard
          title="Alerts firing by severity"
          caption={
            scoped
              ? `Counted from the ${listed.length} listed alerts in the selected subscriptions`
              : `${formatCount(alerts.total)} in the 7-day window · ${formatCount(
                  alerts.resolved_last_7d,
                )} resolved`
          }
          since={alerts.collected_at}
          tableTwin={{
            id: "alert-severity",
            ariaLabel: "Alert counts per severity",
            data: severityRows,
            getRowId: (row) => row.severity,
            columns: [
              {
                id: "severity",
                header: "Severity",
                accessor: (row) => row.severity,
                cell: (row) => (
                  <Badge tone={severityStatus(row.severity).tone} variant="subtle" size="xs">
                    {severityStatus(row.severity).label}
                  </Badge>
                ),
              },
              {
                id: "active",
                header: "Firing now",
                accessor: (row) => row.active,
                align: "end",
                cell: (row) => <span className="tabular-nums">{formatCount(row.active)}</span>,
              },
              {
                id: "total",
                header: "In window",
                accessor: (row) => row.total,
                align: "end",
                cell: (row) => <span className="tabular-nums">{formatCount(row.total)}</span>,
              },
            ],
          }}
        >
          {activeTotal === 0 ? (
            <EmptyState
              size="sm"
              title="No Azure Monitor alerts are firing"
              description="Every alert in the collection window is resolved."
            />
          ) : (
            <div className="flex flex-col gap-3">
              <div className="relative">
                <Chart.Container
                  config={SEVERITY_CONFIG}
                  className="h-64"
                  aria-label="Alerts firing now, by severity"
                >
                  <Chart.PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                    <Chart.Tooltip
                      content={
                        <Chart.TooltipContent formatter={(value) => `${value} firing`} hideLabel />
                      }
                    />
                    <Chart.Pie
                      {...Chart.seriesDefaults}
                      data={donutRows}
                      dataKey="active"
                      nameKey="severity"
                      cx="50%"
                      cy="50%"
                      innerRadius="60%"
                      outerRadius="85%"
                      paddingAngle={2}
                      cornerRadius={3}
                      stroke="var(--norican-surface)"
                      strokeWidth={2}
                      cursor="pointer"
                      onClick={(_, index) => {
                        const row = donutRows[index];
                        if (row) {
                          navigateDrill(CLOUD_ALERTS_PATH, {
                            ...drillParams,
                            severity: row.severity,
                          });
                        }
                      }}
                    >
                      {donutRows.map((row) => (
                        <Chart.Cell key={row.severity} fill={severityStatus(row.severity).color} />
                      ))}
                    </Chart.Pie>
                  </Chart.PieChart>
                </Chart.Container>
                {/* Centre total, per the DS donut recipe — the hole carries the sum. */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
                >
                  <span className="text-fg-muted text-xs font-semibold tracking-wide uppercase">
                    Firing
                  </span>
                  <span className="text-fg text-2xl font-bold">{formatCount(activeTotal)}</span>
                </div>
              </div>
              {/* Severity legend with icons — severity is never colour-alone (§3.2). */}
              <div className="text-fg-muted flex flex-wrap justify-center gap-3 text-xs">
                {donutRows.map((row) => {
                  const status = severityStatus(row.severity);
                  return (
                    <span key={row.severity} className="inline-flex items-center gap-1.5">
                      <Icon icon={status.icon} size="xs" />
                      {status.label} ({row.active})
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </ChartCard>

        <ChartCard
          title="Alerts fired per day"
          caption={`Last ${volumeDays} day${volumeDays === 1 ? "" : "s"}${capCaveat}`}
          since={alerts.collected_at}
          tableTwin={{
            id: "alert-volume",
            ariaLabel: "Alerts fired per day",
            data: volumeRows,
            getRowId: (row) => row.day,
            columns: [
              { id: "day", header: "Day", accessor: (row) => row.label },
              {
                id: "fired",
                header: "Fired",
                accessor: (row) => row.fired,
                align: "end",
                cell: (row) => <span className="tabular-nums">{formatCount(row.fired)}</span>,
              },
            ],
          }}
        >
          <Chart.Container
            config={{ fired: { label: "Alerts fired", color: "var(--norican-chart-1)" } }}
            className="h-64"
            aria-label={`Alerts fired per day, last ${volumeDays} days`}
          >
            <Chart.LineChart data={volumeRows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
              <Chart.XAxis {...Chart.axisProps} dataKey="label" />
              <Chart.YAxis {...Chart.axisProps} width={32} allowDecimals={false} />
              <Chart.Tooltip content={<Chart.TooltipContent />} />
              <Chart.Line
                {...Chart.seriesDefaults}
                dataKey="fired"
                type="monotone"
                stroke="var(--color-fired)"
                strokeWidth={2}
                dot={false}
              />
            </Chart.LineChart>
          </Chart.Container>
        </ChartCard>
      </div>

      <PanelCard
        title="Worst firing alerts"
        caption={
          <DrillLink
            to={CLOUD_ALERTS_PATH}
            params={drillParams}
            className="text-accent text-sm font-medium hover:underline"
          >
            View all {formatCount(alerts.total)} →
          </DrillLink>
        }
      >
        {firing.length === 0 ? (
          <EmptyState
            size="sm"
            title="Nothing is firing"
            description="The most severe alerts would be listed here."
          />
        ) : (
          <DataGrid<CloudAlert>
            data={firing.slice(0, WORST_LIMIT)}
            getRowId={(alert) => alert.id}
            density="compact"
            aria-label={`The ${WORST_LIMIT} most severe alerts currently firing`}
            columns={[
              {
                id: "severity",
                header: "Severity",
                accessor: (alert) => alert.severity,
                cell: (alert) => (
                  <Badge tone={severityStatus(alert.severity).tone} variant="subtle" size="xs">
                    {severityStatus(alert.severity).short}
                  </Badge>
                ),
              },
              {
                id: "name",
                header: "Alert",
                accessor: (alert) => alert.name,
                cell: (alert) => (
                  <div className="flex flex-col">
                    <DrillLink
                      to={CLOUD_ALERTS_PATH}
                      params={{ ...drillParams, [ALERT_DRAWER_PARAM]: alert.id }}
                      className="text-fg font-medium hover:underline"
                    >
                      {alert.name}
                    </DrillLink>
                    {alert.description && (
                      <span className="text-fg-muted text-xs">{alert.description}</span>
                    )}
                  </div>
                ),
              },
              {
                id: "resource",
                header: "Resource",
                accessor: (alert) => resourceNameFromId(alert.target_resource),
                cell: (alert) => (
                  <div className="flex flex-col">
                    <span className="text-fg text-sm">
                      {resourceNameFromId(alert.target_resource)}
                    </span>
                    {alert.target_resource_type && (
                      <span className="text-fg-muted text-xs">
                        {shortType(alert.target_resource_type)}
                      </span>
                    )}
                  </div>
                ),
              },
              {
                id: "subscription",
                header: "Subscription",
                accessor: (alert) => alert.subscription_name,
                cell: (alert) => (
                  <span className="text-fg-muted text-sm">{alert.subscription_name}</span>
                ),
              },
              {
                id: "fired",
                header: "Fired",
                accessor: (alert) => alert.fired_at ?? "",
                cell: (alert) => (
                  <span className="text-fg-muted text-xs tabular-nums">
                    {formatTimestamp(alert.fired_at)}
                  </span>
                ),
              },
            ]}
          />
        )}
        {alerts.errors.length > 0 && (
          <Typography variant="body-sm" tone="muted">
            Collection problems: {alerts.errors.join(" · ")}
          </Typography>
        )}
      </PanelCard>
    </section>
  );
}
